import { useRef } from 'react';
import type {
  PhieuXuatDieuChuyenCellValue,
  PhieuXuatDieuChuyenChiTiet,
  PhieuXuatDieuChuyenDetailUpdate,
  PhieuXuatDieuChuyenVatTuData
} from '@/types/schemas';
import { type TableSelectedCell, useTableRows } from '@/components/custom/erp';
import { useReasonAutoFill } from './useReasonAutoFill';

export type SelectedCellInfo = TableSelectedCell;

export function useDetailRows(
  initialRows: PhieuXuatDieuChuyenChiTiet[] = [],
  onChange?: (rows: PhieuXuatDieuChuyenChiTiet[]) => void,
  onFieldValueChange?: (
    row: PhieuXuatDieuChuyenChiTiet,
    field: string,
    value: PhieuXuatDieuChuyenCellValue
  ) => PhieuXuatDieuChuyenDetailUpdate
) {
  const fieldUpdateRef = useRef<
    (
      row: PhieuXuatDieuChuyenChiTiet,
      field: string,
      value: PhieuXuatDieuChuyenCellValue
    ) => PhieuXuatDieuChuyenDetailUpdate
  >(() => ({}));
  const { handleFieldUpdates } = useReasonAutoFill();

  const {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handleMoveRow,
    handlePasteRow,
    setNonWritableFields,
    setPasteConfigs,
    setSelectedCell,
    setSelectedRow,
    setSelectedRowUuid
  } = useTableRows<PhieuXuatDieuChuyenChiTiet>({
    initialRows,
    defaultRows: 1,
    onChange,
    onFieldValueChange: (row, field, value) => fieldUpdateRef.current(row, field, value),
    defaultField: 'ma_vt'
  });

  const applyFieldUpdates = (row: PhieuXuatDieuChuyenChiTiet, field: string, value: PhieuXuatDieuChuyenCellValue) => {
    const extraFields = onFieldValueChange?.(row, field, value) ?? {};
    const updatesFromReason = handleFieldUpdates({
      row,
      field,
      value,
      setters: {
        setRows,
        setSelectedRow,
        onChange,
        selectedRowUuid
      }
    });
    if (field === 'ma_vt_data') {
      const productValue =
        value && typeof value === 'object' && 'uuid' in value ? (value as PhieuXuatDieuChuyenVatTuData) : null;
      extraFields.dvt = productValue?.dvt_nl_data?.uuid || productValue?.dvt_data?.uuid || row.dvt;
    }

    return {
      ...extraFields,
      ...updatesFromReason
    };
  };

  fieldUpdateRef.current = applyFieldUpdates;

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    setSelectedRow,
    setSelectedCell,
    setSelectedRowUuid
  };
}
